// T is the data response
// U is options for the query
// V is an array of data results
export class Cursor {
    constructor(_options, loadFn, nextFn, _cacheKey, cache, opts) {
        Object.defineProperty(this, "_options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: _options
        });
        Object.defineProperty(this, "loadFn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: loadFn
        });
        Object.defineProperty(this, "nextFn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: nextFn
        });
        Object.defineProperty(this, "_cacheKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: _cacheKey
        });
        Object.defineProperty(this, "cache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: cache
        });
        Object.defineProperty(this, "opts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: opts
        });
        Object.defineProperty(this, "_hasMore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "current", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ready", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "refresh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (options) => {
                const optsCopy = this.opts?.optsClean?.(this._options) ?? { ...this._options, ...options };
                const cacheKey = this.cacheKey();
                const results = await this._load(cacheKey, optsCopy);
                const { opts, hasMore, newData } = this.nextFn(results, optsCopy);
                if (opts) {
                    this._options = { ...optsCopy, ...opts };
                }
                this._hasMore = hasMore;
                this.current = [...newData];
                this.ready = true;
                return this.current;
            }
        });
        Object.defineProperty(this, "next", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const optsCopy = { ...this._options };
                const cacheKey = this.cacheKey();
                let results;
                const cachedData = this.cache.get(cacheKey);
                if (cachedData?.data) {
                    results = cachedData.data;
                }
                else if (cachedData?.error) {
                    throw cachedData.error;
                }
                else {
                    results = await this._load(cacheKey, optsCopy);
                }
                const { opts, hasMore, newData } = this.nextFn(results, optsCopy);
                if (opts) {
                    this._options = { ...optsCopy, ...opts };
                }
                this._hasMore = hasMore;
                this.current = [...(this.current || []), ...newData];
                this.ready = true;
                return this.current;
            }
        });
        // We need prime since adding/removing things to the cache directly causes infinite loops.
        Object.defineProperty(this, "prime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (results) => {
                const optsCopy = { ...this._options };
                const { opts, hasMore, newData } = this.nextFn(results, optsCopy);
                if (opts) {
                    this._options = { ...optsCopy, ...opts };
                }
                this._hasMore = hasMore;
                this.current = [...(this.current || []), ...newData];
                this.ready = true;
                return this.current;
            }
        });
        // Prime cache if initial data provided
        if (opts?.initialData) {
            this.prime(opts?.initialData);
        }
        // Check cache before issuing API call to prevent duplicate load of data
        else if (cache.has(this.cacheKey())) {
            const cacheData = cache.get(this.cacheKey());
            if (cacheData && cacheData.data) {
                this.next().then(() => cache.delete(this.cacheKey()));
            }
        }
    }
    options() {
        return this._options;
    }
    hasMore() {
        return this._hasMore;
    }
    cacheKey() {
        return this._cacheKey(this._options);
    }
    async _load(cacheKey, opts) {
        try {
            const results = await this.loadFn(opts);
            if (typeof window === "undefined") {
                this.cache.set(cacheKey, { data: results });
            }
            return results;
        }
        catch (error) {
            if (typeof window === "undefined") {
                this.cache.set(cacheKey, { error: error.toString() });
            }
            throw error;
        }
    }
}
//# sourceMappingURL=cursor.js.map